<script setup lang="ts">
import { useEventListener } from '@vueuse/core';
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { AppShell, IconLogo, IconMenu } from '@/shared/ui';
import { BaseSidePanel } from '@/shared/ui/side-panel';

import PlatformSearchDialog from './PlatformSearchDialog.vue';
import PlatformSidebar from './PlatformSidebar.vue';

const route = useRoute();
const isMenuOpen = ref(false);
const isSearchOpen = ref(false);

const openSearch = (): void => {
  isMenuOpen.value = false;
  isSearchOpen.value = true;
};

// ⌘K живёт только на hq-хосте: поиск SOP с тем же сочетанием работает на тенантном.
useEventListener(globalThis.window, 'keydown', (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    openSearch();
  }
});

watch(
  () => route.fullPath,
  () => {
    isMenuOpen.value = false;
  },
);
</script>

<template>
  <AppShell class="lg:pl-[232px]">
    <template #header>
      <header
        class="sticky top-0 z-30 flex min-h-14 items-center gap-2 border-b border-white/8 bg-(--base-main-bg)/92 px-2 backdrop-blur-xl lg:hidden"
        data-pull-to-refresh-ignore
      >
        <button
          type="button"
          class="flex min-h-11 min-w-11 items-center justify-center rounded-xl text-(--base-secondary-text-color) hover:bg-white/5 hover:text-(--base-text-color) focus-visible:ring-2 focus-visible:ring-(--base-accent-color) focus-visible:outline-none"
          aria-label="Открыть меню"
          @click="isMenuOpen = true"
        >
          <IconMenu
            :width="20"
            :height="20"
          />
        </button>
        <RouterLink
          :to="{ name: 'platform.overview' }"
          class="flex min-h-11 items-center gap-2 rounded-xl pr-2"
        >
          <IconLogo
            :width="28"
            :height="28"
            class="text-(--base-accent-color)"
          />
          <span class="text-base font-semibold tracking-tight">Морс</span>
          <span class="text-sm text-(--base-secondary-text-color)">Штаб</span>
        </RouterLink>
      </header>

      <aside
        class="fixed inset-y-0 left-0 z-30 hidden w-[232px] border-r border-white/8 bg-(--base-main-bg) lg:block"
        data-pull-to-refresh-ignore
      >
        <PlatformSidebar @search="openSearch" />
      </aside>
    </template>

    <RouterView />

    <template #overlay>
      <BaseSidePanel
        :open="isMenuOpen"
        side="left"
        width="280px"
        label="Меню штаба"
        @close="isMenuOpen = false"
      >
        <PlatformSidebar @search="openSearch" />
      </BaseSidePanel>

      <PlatformSearchDialog
        v-if="isSearchOpen"
        v-model:show="isSearchOpen"
      />
    </template>
  </AppShell>
</template>
